import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Navigate, useParams } from "react-router-dom";
import bulltickLogo from "../../assets/images/bt_logo.png";
import DoneStep from "./components/DoneStep";
import FormStep from "./components/FormStep";
import ReviewStep from "./components/ReviewStep";
import SectionListStep from "./components/SectionListStep";
import SelectionStep from "./components/SelectionStep";
import WelcomeStep from "./components/WelcomeStep";
import { deleteEstateMappingDocument, fetchEstateMappingDocuments, fetchEstateMappingSession, saveEstateMappingSubmission, sendEstateMappingOtp, submitEstateMappingSubmission, uploadEstateMappingDocument, validateEstateMappingOtp } from "./api";
import { internalization } from "../../constants";
import { getEstateMappingCopy } from "./copy";
import { ORDER, getEstateMappingFieldLabels, getEstateMappingPillars, getEstateMappingSections } from "./data";
import { buildEstateMappingPayload, emptyInitialData } from "./utils";

const normalizeLanguage = (language) => (/^es/.test(language || "") ? "es" : "en");

const COUNTRY_ALIASES = {
  "Estados Unidos": "United States",
  Espana: "Spain",
  "España": "Spain",
  Otro: "Other",
};

const OTHER_ASSET_TYPE_ALIASES = {
  Criptomonedas: "Cryptocurrency",
  "Metales preciosos": "Other Precious Metals",
  Otro: "Others",
  Other: "Others",
};

const STRUCTURE_TYPE_ALIASES = {
  Corporacion: "Corporation",
  Fundacion: "Foundation",
  "Sociedad Civil": "Civil Partnership",
  Otro: "Other",
};

const createEstateMappingId = (section) => `${section.payloadKey}_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;

const emptyOwnershipStructure = () => ({
  class: "GraphLinksModel",
  linkKeyProperty: "key",
  nodeDataArray: [],
  linkDataArray: [],
});

const otpSessionKey = (token) => `estate_mapping_otp_sent:${token}`;

const getStoredOtpRequest = (token) => {
  try {
    const stored = window.sessionStorage.getItem(otpSessionKey(token));
    return stored ? JSON.parse(stored) : null;
  } catch (_) {
    return null;
  }
};

const storeOtpRequest = (token, response) => {
  try {
    const current = getStoredOtpRequest(token) || {};
    window.sessionStorage.setItem(otpSessionKey(token), JSON.stringify({
      ...current,
      ...response,
    }));
    return true;
  } catch (_) {
    return false;
  }
};

const OtpStep = ({ copy, code, error, phoneNumber, submitting, onChange, onResend, onSubmit }) => (
  <section className="estate-mapping__otp">
    <h1>{copy.otp.title}</h1>
    <p dangerouslySetInnerHTML={{ __html: copy.otp.body.replace("{phone_number}", phoneNumber || "") }} />
    <form onSubmit={onSubmit}>
      <input
        autoComplete="one-time-code"
        autoFocus
        maxLength="12"
        onChange={(event) => onChange(event.target.value)}
        placeholder={copy.otp.placeholder}
        type="text"
        value={code}
      />
      {error && <span>{error}</span>}
      <button className="estate-mapping__button estate-mapping__button--primary" disabled={submitting} type="submit">
        {submitting ? copy.otp.validating : copy.otp.validate}
      </button>
    </form>
    <div>
      <p>{copy.otp.resend_text}</p>
      <button disabled={submitting} onClick={onResend} type="button">{copy.otp.resend}</button>
    </div>
  </section>
);

const normalizeEstateMappingItem = (item, sectionId) => {
  const normalized = { ...item };
  if (!normalized.currency_type && normalized.moneda) normalized.currency_type = normalized.moneda;
  if (!normalized.valuation && normalized.currency_value) normalized.valuation = normalized.currency_value;
  if (["inversiones_alternativas", "otros_activos"].includes(sectionId) && !normalized.valuation && normalized.valor) {
    normalized.valuation = normalized.valor;
    delete normalized.valor;
  }
  if (normalized.pais && COUNTRY_ALIASES[normalized.pais]) normalized.pais = COUNTRY_ALIASES[normalized.pais];
  if (sectionId === "otros_activos" && normalized.tipo && OTHER_ASSET_TYPE_ALIASES[normalized.tipo]) normalized.tipo = OTHER_ASSET_TYPE_ALIASES[normalized.tipo];
  if (sectionId === "estructuras" && normalized.tipo && STRUCTURE_TYPE_ALIASES[normalized.tipo]) normalized.tipo = STRUCTURE_TYPE_ALIASES[normalized.tipo];
  delete normalized.moneda;
  delete normalized.currency_value;
  return normalized;
};

const dataFromPayload = (payload, sections, availableSectionIds = ORDER) => (
  ORDER.reduce((result, sectionId) => {
    if (!availableSectionIds.includes(sectionId)) return result;

    const payloadKey = sections[sectionId].payloadKey;
    if (!Array.isArray(payload[payloadKey])) return result;

    return {
      ...result,
    [sectionId]: payload[payloadKey]
      .filter((item) => sectionId !== "estructuras" || !String(item.estate_mapping_id || "").startsWith("holder_structure_"))
      .map((item, index) => ({
          ...normalizeEstateMappingItem(item, sectionId),
          estate_mapping_id: item.estate_mapping_id || `${payloadKey}:${index}`,
          [sections[sectionId].nameKey]: item[sections[sectionId].nameKey] || item.name || "",
        })),
    };
  }, emptyInitialData())
);

const selectedFromPayload = (payload, sections) => {
  const sectionByPayloadKey = ORDER.reduce((result, sectionId) => ({
    ...result,
    [sections[sectionId].payloadKey]: sectionId,
  }), {});

  if (Array.isArray(payload.selected_sections)) {
    return payload.selected_sections.map((payloadKey) => sectionByPayloadKey[payloadKey]).filter(Boolean);
  }

  return ORDER.filter((sectionId) => Array.isArray(payload[sections[sectionId].payloadKey]) && payload[sections[sectionId].payloadKey].length > 0);
};

const EstateMappingScreen = () => {
  const { token } = useParams();
  const [estateMappingSession, setEstateMappingSession] = useState(null);
  const [otpToken, setOtpToken] = useState("");
  const [otpPhoneNumber, setOtpPhoneNumber] = useState("");
  const [otpCode, setOtpCode] = useState("");
  const [otpError, setOtpError] = useState("");
  const [otpSubmitting, setOtpSubmitting] = useState(false);
  const [languageUserAgent, setLanguageUserAgent] = useState(normalizeLanguage(window.navigator.language));
  const [loadingSession, setLoadingSession] = useState(!!token);
  const [sessionError, setSessionError] = useState(false);
  const copy = useMemo(() => getEstateMappingCopy(internalization[languageUserAgent]), [languageUserAgent]);
  const sections = useMemo(() => getEstateMappingSections(copy), [copy]);
  const pillars = useMemo(() => getEstateMappingPillars(copy), [copy]);
  const fieldLabels = useMemo(() => getEstateMappingFieldLabels(sections), [sections]);
  const order = ORDER;

  const [step, setStep] = useState("welcome");
  const [selected, setSelected] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [data, setData] = useState(emptyInitialData);
  const [editing, setEditing] = useState(null);
  const [cameFromReview, setCameFromReview] = useState(false);
  const [sessionLoaded, setSessionLoaded] = useState(!token);
  const [documents, setDocuments] = useState([]);
  const [ownershipStructure, setOwnershipStructure] = useState(emptyOwnershipStructure);
  const [estateMappingInternalization, setEstateMappingInternalization] = useState({});
  const availableSectionIds = useMemo(() => {
    const enabledSections = estateMappingSession && estateMappingSession.enabled_sections;

    // Older API responses have no configuration and intentionally retain the
    // previous behaviour: every card remains available.
    return Array.isArray(enabledSections) && enabledSections.length > 0
      ? ORDER.filter((sectionId) => enabledSections.includes(sectionId))
      : ORDER;
  }, [estateMappingSession]);

  const updateOwnershipStructure = useCallback((nextOwnershipStructure) => {
    setOwnershipStructure((current) => (
      JSON.stringify(current) === JSON.stringify(nextOwnershipStructure) ? current : nextOwnershipStructure
    ));
  }, []);

  const currentSectionId = selected[currentIndex];
  const progress = useMemo(() => {
    if (!["section", "form", "review"].includes(step)) return null;
    const currentStep = step === "review" ? selected.length + 1 : currentIndex + 1;
    const total = selected.length + 1;
    return { currentStep, total, width: `${Math.round((currentStep / total) * 100)}%` };
  }, [currentIndex, selected.length, step]);

  useEffect(() => {
    const payload = buildEstateMappingPayload(selected, data, order, sections, ownershipStructure);
    window.__estateMappingLastPayload = payload;
    console.log("Estate mapping current payload", payload);
  }, [data, order, ownershipStructure, sections, selected]);

  useEffect(() => {
    if (!token) return;

    const storedOtpRequest = getStoredOtpRequest(token);
    if (storedOtpRequest) {
      setOtpPhoneNumber(storedOtpRequest.phone_number || "");
      if (storedOtpRequest.language) setLanguageUserAgent(normalizeLanguage(storedOtpRequest.language));
      if (storedOtpRequest.otp_token) {
        setOtpToken(storedOtpRequest.otp_token);
        setLoadingSession(true);
      } else {
        setLoadingSession(false);
      }
      return;
    }

    sendEstateMappingOtp(token)
      .then((response) => {
        setOtpPhoneNumber(response.phone_number || "");
        storeOtpRequest(token, response);
        setLanguageUserAgent(normalizeLanguage(response.language));
        setSessionError(false);
      })
      .catch(() => {
        setSessionError(true);
      })
      .finally(() => {
        setLoadingSession(false);
      });
  }, [token]);

  useEffect(() => {
    if (!token || !otpToken || sessionLoaded) return;

    fetchEstateMappingSession(token, otpToken)
      .then((response) => {
        const payload = response.payload || {};
        setEstateMappingSession(response);
        setEstateMappingInternalization(response.internalization || {});
        setOwnershipStructure(payload.ownership_structure || emptyOwnershipStructure());
        setLanguageUserAgent(normalizeLanguage(response.language));
        const enabledSections = Array.isArray(response.enabled_sections) && response.enabled_sections.length > 0
          ? response.enabled_sections
          : ORDER;
        const nextAvailableSectionIds = ORDER.filter((sectionId) => enabledSections.includes(sectionId));
        const nextData = dataFromPayload(payload, sections, nextAvailableSectionIds);
        setSelected(selectedFromPayload(payload, sections).filter((sectionId) => nextAvailableSectionIds.includes(sectionId)));
        setData(nextData);
        setSessionLoaded(true);
        setSessionError(false);
        if (response.submission_id) {
          fetchEstateMappingDocuments(response.submission_id, token, otpToken)
            .then((documentsResponse) => setDocuments(documentsResponse.documents || []))
            .catch((error) => console.log("Estate mapping documents fetch error", error));
        }
      })
      .catch(() => {
        setSessionError(true);
      })
      .finally(() => {
        setLoadingSession(false);
      });
  }, [otpToken, sections, sessionLoaded, token]);

  useEffect(() => {
    if (!sessionLoaded || !estateMappingSession || !estateMappingSession.submission_id) return;

    const payload = buildEstateMappingPayload(selected, data, order, sections, ownershipStructure);
    saveEstateMappingSubmission(estateMappingSession.submission_id, token, otpToken, payload)
      .then((response) => {
        const nextOwnershipStructure = response.payload && response.payload.ownership_structure;
        if (nextOwnershipStructure) {
          updateOwnershipStructure(nextOwnershipStructure);
        }
      })
      .catch((error) => console.log("Estate mapping save error", error));
  }, [data, estateMappingSession, order, otpToken, sections, selected, sessionLoaded, token, updateOwnershipStructure]);

  const toggleSelection = (id) => setSelected((current) => (
    !availableSectionIds.includes(id)
      ? current
      : current.includes(id) ? current.filter((item) => item !== id) : current.concat(id)
  ));

  const startSections = () => {
    const ordered = selected.slice().sort((a, b) => ORDER.indexOf(a) - ORDER.indexOf(b));
    setSelected(ordered);
    setCurrentIndex(0);
    setStep("section");
  };

  const openForm = (index, fromReview) => {
    setEditing({ sectionId: currentSectionId, index });
    setCameFromReview(!!fromReview);
    setStep("form");
  };

  const closeForm = () => setStep(cameFromReview ? "review" : "section");

  const saveForm = (formState, manualStructures = [], manualLiabilities = []) => {
    setData((current) => {
      const sectionData = (current[editing.sectionId] || []).slice();
      const record = {
        ...formState,
        estate_mapping_id: formState.estate_mapping_id || createEstateMappingId(sections[editing.sectionId]),
      };
      if (editing.index === null) sectionData.push(record);
      else sectionData[editing.index] = record;
      const structures = (current.estructuras || []).slice();
      manualStructures.forEach((structure) => {
        const index = structures.findIndex((item) => item.estate_mapping_id === structure.estate_mapping_id);
        if (index === -1) structures.push(structure);
        else structures[index] = { ...structures[index], ...structure };
      });
      const liabilities = (current.pasivos || []).slice();
      manualLiabilities.forEach((liability) => {
        const index = liabilities.findIndex((item) => item.estate_mapping_id === liability.estate_mapping_id);
        if (index === -1) liabilities.push(liability);
        else liabilities[index] = { ...liabilities[index], ...liability };
      });
      const nextData = {
        ...current,
        [editing.sectionId]: sectionData,
        ...(manualStructures.length > 0 ? { estructuras: structures } : {}),
        ...(manualLiabilities.length > 0 ? { pasivos: liabilities } : {}),
      };
      if (manualStructures.length > 0 || manualLiabilities.length > 0) {
        setSelected((currentSelected) => (
          currentSelected.concat(
            manualStructures.length > 0 && !currentSelected.includes("estructuras") ? ["estructuras"] : [],
            manualLiabilities.length > 0 && !currentSelected.includes("pasivos") ? ["pasivos"] : []
          ).sort((a, b) => ORDER.indexOf(a) - ORDER.indexOf(b))
        ));
      }
      return nextData;
    });
    closeForm();
  };

  const deleteItem = (index) => {
    setData((current) => {
      const sectionData = (current[currentSectionId] || []).slice();
      sectionData.splice(index, 1);
      return { ...current, [currentSectionId]: sectionData };
    });
  };

  const uploadDocument = (sectionId, recordId, file) => {
    if (!estateMappingSession || !estateMappingSession.submission_id) return Promise.resolve();

    return uploadEstateMappingDocument(estateMappingSession.submission_id, token, otpToken, sectionId, recordId, file)
      .then((response) => setDocuments((current) => current.concat(response.document)))
      .catch((error) => console.log("Estate mapping document upload error", error));
  };

  const deleteDocument = (documentId) => {
    if (!estateMappingSession || !estateMappingSession.submission_id) return;

    deleteEstateMappingDocument(estateMappingSession.submission_id, token, otpToken, documentId)
      .then(() => setDocuments((current) => current.filter((document) => document.id !== documentId)))
      .catch((error) => console.log("Estate mapping document delete error", error));
  };

  const resendOtp = () => {
    if (otpSubmitting) return;

    setOtpSubmitting(true);
    setOtpError("");
    sendEstateMappingOtp(token)
      .then((response) => {
        setOtpPhoneNumber(response.phone_number || otpPhoneNumber);
        storeOtpRequest(token, {
          language: response.language || languageUserAgent,
          phone_number: response.phone_number || otpPhoneNumber,
        });
      })
      .catch(() => setOtpError(copy.otp.send_error))
      .finally(() => setOtpSubmitting(false));
  };

  const validateOtp = (event) => {
    event.preventDefault();
    if (!otpCode || otpCode.trim().length === 0) {
      setOtpError(copy.otp.blank_error);
      return;
    }

    setOtpSubmitting(true);
    setOtpError("");
    validateEstateMappingOtp(token, otpCode)
      .then((response) => {
        storeOtpRequest(token, {
          otp_token: response.otp_token,
        });
        setLoadingSession(true);
        setOtpToken(response.otp_token);
      })
      .catch(() => setOtpError(copy.otp.invalid_error))
      .finally(() => setOtpSubmitting(false));
  };

  const goNext = () => {
    if (cameFromReview) {
      setCameFromReview(false);
      setStep("review");
    } else if (currentIndex < selected.length - 1) {
      setCurrentIndex(currentIndex + 1);
    } else {
      setStep("review");
    }
  };

  const goBack = () => {
    if (cameFromReview) {
      setCameFromReview(false);
      setStep("review");
    } else if (currentIndex === 0) {
      setStep("selection");
    } else {
      setCurrentIndex(currentIndex - 1);
    }
  };

  const addSectionFromReview = (sectionId) => {
    if (!availableSectionIds.includes(sectionId)) return;

    const ordered = selected.concat(sectionId).sort((a, b) => ORDER.indexOf(a) - ORDER.indexOf(b));
    setSelected(ordered);
    setCurrentIndex(ordered.indexOf(sectionId));
    setCameFromReview(true);
    setStep("section");
  };

  const editFromReview = (sectionId, index) => {
    setCurrentIndex(selected.indexOf(sectionId));
    setEditing({ sectionId, index });
    setCameFromReview(true);
    setStep("form");
  };

  const submit = () => {
    const payload = buildEstateMappingPayload(selected, data, order, sections, ownershipStructure);
    window.__estateMappingLastPayload = payload;
    console.log("Estate mapping submit payload", payload);
    if (estateMappingSession && estateMappingSession.submission_id) {
      submitEstateMappingSubmission(estateMappingSession.submission_id, token, otpToken, payload)
        .then(() => setStep("done"))
        .catch((error) => console.log("Estate mapping submit error", error));
    } else {
      setStep("done");
    }
  };

  const goToSelection = () => {
    setEditing(null);
    setCameFromReview(false);
    setCurrentIndex(0);
    setStep("selection");
  };

  if (loadingSession) {
    return (
      <div className="estate-mapping">
        <div className="estate-mapping__shell">
          <header className="estate-mapping__topbar">
            <div><img src={bulltickLogo} alt="Bulltick" /><span>{copy.title}</span></div>
          </header>
          <div className="estate-mapping__empty">{copy.loading || "Loading..."}</div>
        </div>
      </div>
    );
  }

  if (sessionError) {
    return (
      <div className="estate-mapping">
        <div className="estate-mapping__shell">
          <header className="estate-mapping__topbar">
            <div><img src={bulltickLogo} alt="Bulltick" /><span>{copy.title}</span></div>
          </header>
          <div className="estate-mapping__empty">{copy.invalid_link || "Invalid link"}</div>
        </div>
      </div>
    );
  }

  if (token && !otpToken) {
    return (
      <div className="estate-mapping">
        <div className="estate-mapping__shell">
          <header className="estate-mapping__topbar">
            <div><img src={bulltickLogo} alt="Bulltick" /><span>{copy.title}</span></div>
          </header>
          <OtpStep
            code={otpCode}
            copy={copy}
            error={otpError}
            onChange={(value) => { setOtpCode(value); setOtpError(""); }}
            onResend={resendOtp}
            onSubmit={validateOtp}
            phoneNumber={otpPhoneNumber}
            submitting={otpSubmitting}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="estate-mapping">
      <div className="estate-mapping__shell">
        <header className="estate-mapping__topbar">
          <button className="estate-mapping__brand" onClick={goToSelection} type="button">
            <img src={bulltickLogo} alt="Bulltick" /><span>{copy.title}{estateMappingSession && estateMappingSession.name ? ` - ${estateMappingSession.name}` : ""}</span>
          </button>
          {progress && <span>{copy.step} {progress.currentStep} {copy.of} {progress.total}</span>}
        </header>
        {progress && step !== "selection" && <div className="estate-mapping__progress"><i style={{ width: progress.width }} /></div>}
        {step === "welcome" && <WelcomeStep copy={copy} name={estateMappingSession && estateMappingSession.name} onStart={() => setStep("selection")} />}
        {step === "selection" && <SelectionStep copy={copy} pillars={pillars} sections={sections} selected={selected} availableSectionIds={availableSectionIds} onToggle={toggleSelection} onContinue={startSections} />}
        {step === "section" && currentSectionId && (
          <SectionListStep
            sectionId={currentSectionId}
            sections={sections}
            copy={copy}
            items={data[currentSectionId] || []}
            onBack={goBack}
            onAdd={() => openForm(null, false)}
            onEdit={(index) => openForm(index, false)}
            onDelete={deleteItem}
            onNext={goNext}
          />
        )}
        {step === "form" && editing && (
          <FormStep
            sectionId={editing.sectionId}
            sections={sections}
            copy={copy}
            createRecordId={createEstateMappingId}
            data={data}
            documents={documents}
            documentsEnabled={!!(estateMappingSession && estateMappingSession.submission_id)}
            item={editing.index === null ? null : (data[editing.sectionId] || [])[editing.index]}
            onDeleteDocument={deleteDocument}
            onUploadDocument={uploadDocument}
            onCancel={closeForm}
            onSave={saveForm}
            returnToReview={cameFromReview}
          />
        )}
        {step === "review" && (
          <ReviewStep
            selected={selected}
            data={data}
            documents={documents}
            ownershipStructure={ownershipStructure}
            internalization={estateMappingInternalization}
            copy={copy}
            fieldLabels={fieldLabels}
            order={availableSectionIds}
            sections={sections}
            onBack={() => { setCurrentIndex(selected.length - 1); setStep("section"); }}
            onEdit={editFromReview}
            onAddSection={addSectionFromReview}
            onOwnershipStructureChange={updateOwnershipStructure}
            onSubmit={submit}
          />
        )}
        {step === "done" && <DoneStep copy={copy} onReview={() => setStep("review")} />}
      </div>
    </div>
  );
};

export default EstateMappingScreen;
